import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Link2, Loader2, Share2, Trash2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { toast } from '../ui/sonner';
import { Aide } from '../ui/aide';
import { adresseDuLien, lienSuiviAPI } from '../../lib/lienSuiviApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate } from '../../lib/formats';

const AIDE =
  'Le lien ouvre une page de suivi sans compte : le trajet, l’avancement et la dernière position du camion, jamais un montant. Il s’arrête quand vous le retirez.';

/** Créer, copier, envoyer ou retirer le lien de suivi d'un voyage. */
export const LienSuiviDialog = ({ open, onOpenChange, tripId }) => {
  const [lien, setLien] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      setLien(await lienSuiviAPI.lire(tripId));
    } catch (err) {
      setLien(null);
      toast.error(messageErreur(err, 'Le lien n’a pas pu être lu'));
    } finally {
      setChargement(false);
    }
  }, [tripId]);

  useEffect(() => {
    if (open && tripId) charger();
  }, [open, tripId, charger]);

  const agir = async (action, messageSucces, messageErreurRepli) => {
    setEnCours(true);
    try {
      setLien(await action(tripId));
      toast.success(messageSucces);
    } catch (err) {
      toast.error(messageErreur(err, messageErreurRepli));
    } finally {
      setEnCours(false);
    }
  };

  const adresse = adresseDuLien(lien);

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(adresse);
      toast.success('Le lien est copié');
    } catch {
      toast.error('Le lien n’a pas pu être copié');
    }
  };

  const envoyer = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(adresse)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="lien-suivi-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Lien de suivi
            <Aide titre="Lien de suivi">{AIDE}</Aide>
          </DialogTitle>
          <DialogDescription className="sr-only">Lien public de suivi du voyage</DialogDescription>
        </DialogHeader>

        {chargement ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Chargement" />
          </div>
        ) : lien?.active ? (
          <div className="space-y-3">
            <Input readOnly value={adresse} aria-label="Adresse du lien" data-testid="lien-suivi-adresse" onFocus={(e) => e.target.select()} />
            {lien.expires_at ? (
              <p className="text-xs text-muted-foreground" data-testid="lien-suivi-fin">
                Valable jusqu’au {formatDate(lien.expires_at)}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={copier} data-testid="lien-suivi-copier">
                <Copy className="h-4 w-4" aria-hidden="true" />
                Copier
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={envoyer} data-testid="lien-suivi-whatsapp">
                <Share2 className="h-4 w-4" aria-hidden="true" />
                Envoyer sur WhatsApp
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={enCours}
                onClick={() => agir(lienSuiviAPI.retirer, 'Le lien est retiré', 'Le lien n’a pas pu être retiré')}
                data-testid="lien-suivi-retirer"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Retirer le lien
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center py-4">
            <Button
              type="button"
              disabled={enCours || chargement}
              onClick={() => agir(lienSuiviAPI.creer, 'Le lien est créé', 'Le lien n’a pas pu être créé')}
              data-testid="lien-suivi-creer"
            >
              <Link2 className="h-4 w-4" aria-hidden="true" />
              Créer le lien
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LienSuiviDialog;
